import { useEffect, useState } from "react";
import { CloudOff, RefreshCw, Cloud } from "lucide-react";
import {
  getOnlineState,
  subscribeOnline,
  subscribeQueue,
  getQueueSize,
  flushQueue,
} from "@/lib/offline";
import { api } from "@/lib/api";

/**
 * Banner global que avisa cuando la app pierde conexión y permite
 * reintentar manualmente las mutaciones encoladas.
 */
export function OfflineBanner() {
  const [online, setOnline] = useState(getOnlineState());
  const [queue, setQueue] = useState(getQueueSize());
  const [showJustSynced, setShowJustSynced] = useState(false);

  useEffect(() => {
    const unsubOn = subscribeOnline(setOnline);
    const unsubQ = subscribeQueue(setQueue);
    return () => {
      unsubOn();
      unsubQ();
    };
  }, []);

  // Al volver online, intenta vaciar la cola automáticamente
  useEffect(() => {
    if (online && queue > 0) {
      flushQueue(api).then(({ ok }) => {
        if (ok > 0) {
          setShowJustSynced(true);
          setTimeout(() => setShowJustSynced(false), 3500);
        }
      });
    }
  }, [online]);

  if (online && queue === 0 && !showJustSynced) return null;

  if (!online) {
    return (
      <div
        className="fixed top-0 left-0 right-0 z-[200] bg-[#C07F67] text-white text-sm py-1.5 px-4 flex items-center justify-center gap-2 shadow"
        data-testid="offline-banner"
      >
        <CloudOff className="h-4 w-4" />
        <span>
          <strong>Sin conexión</strong> · trabajando en modo offline. Los cambios se guardarán y enviarán automáticamente al volver internet.
          {queue > 0 && <span className="ml-2 bg-white/20 rounded px-1.5 py-0.5 text-xs">{queue} cambio{queue !== 1 ? "s" : ""} en cola</span>}
        </span>
      </div>
    );
  }

  if (queue > 0) {
    return (
      <div
        className="fixed top-0 left-0 right-0 z-[200] bg-[#D4A574] text-white text-sm py-1.5 px-4 flex items-center justify-center gap-3 shadow"
        data-testid="offline-pending-banner"
      >
        <RefreshCw className="h-4 w-4 animate-spin" />
        <span>
          Sincronizando {queue} cambio{queue !== 1 ? "s" : ""} pendiente{queue !== 1 ? "s" : ""}…
        </span>
        <button
          onClick={() => flushQueue(api)}
          className="text-xs underline hover:no-underline"
          data-testid="offline-retry-btn"
        >
          Reintentar ahora
        </button>
      </div>
    );
  }

  // Confirmación efímera al volver online y sincronizar OK
  return (
    <div
      className="fixed top-0 left-0 right-0 z-[200] bg-[#4A5D4E] text-white text-sm py-1.5 px-4 flex items-center justify-center gap-2 shadow"
      data-testid="offline-synced-banner"
    >
      <Cloud className="h-4 w-4" />
      <span>Conexión recuperada — cambios sincronizados ✓</span>
    </div>
  );
}
